import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Contact, Plus } from "shared/assets/icons";
import { Button, Card, ContactTeaser, Loader, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingContactDetails } from "Bands/utils";

const contactCopy = copy.userEmergencyContact;

export const EmergencyContacts: FC = () => {
  const {
    emergencyContacts,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteModal,
    getItemActions,
  } = useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.emergencyContact,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.emergencyContact,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingEmergencyContacts } = useFetchingContactDetails();

  const content = useMemo(() => {
    if (fetchingEmergencyContacts) {
      return <Loader />;
    }

    if (!emergencyContacts?.length) {
      return <Message message={contactCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {emergencyContacts?.map(contact => (
          <ContactTeaser
            {...contact}
            key={contact.id}
            phone={contact.phoneNumber}
            actions={getActions(contact.id)}
          />
        ))}
      </GridContainer>
    );
  }, [emergencyContacts, fetchingEmergencyContacts, getActions]);

  return (
    <Card icon={Contact} title={contactCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.emergencyContact)}
          data-testid="add_contact_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{contactCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
